/**
 * A filter drop-down that takes several values: a select-looking button that
 * opens a checkbox list. Nothing ticked means "all", which the button says in
 * `allLabel`. A long list (actors, actions) gets a search box over it with
 * `searchable`. Positioning and closing come from `usePopover`.
 */

import { useId, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';

import { usePopover } from './usePopover';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** A trailing count or note, e.g. how many ideas the value holds. */
  meta?: string | number;
}

function summaryOf(picked: MultiSelectOption[], allLabel: string, noun: string, listUpTo: number): string {
  if (picked.length === 0) return allLabel;
  if (picked.length <= listUpTo) return picked.map((o) => o.label).join(', ');
  return `${picked.length} ${noun}`;
}

function OptionList({
  noun,
  shown,
  value,
  onToggle,
}: {
  noun: string;
  shown: MultiSelectOption[];
  value: string[];
  onToggle: (option: string) => void;
}) {
  return (
    <div className="multi-select-options">
      {shown.length === 0 && <div className="multi-select-none hint">No {noun} match</div>}
      {shown.map((o) => (
        <label key={o.value} className="dropdown-item multi-select-option">
          <input type="checkbox" checked={value.includes(o.value)} onChange={() => onToggle(o.value)} />
          <span>{o.label}</span>
          {o.meta !== undefined && <span className="multi-select-meta">{o.meta}</span>}
        </label>
      ))}
    </div>
  );
}

function SearchBox({ noun, needle, onChange }: { noun: string; needle: string; onChange: (needle: string) => void }) {
  return (
    <div className="search-field multi-select-search">
      <Search aria-hidden="true" />
      <input
        type="search"
        value={needle}
        placeholder={`Search ${noun}`}
        aria-label={`Search ${noun}`}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function MultiSelect({
  label,
  allLabel,
  noun,
  options,
  value,
  onChange,
  searchable = false,
  listUpTo = 1,
}: {
  /** Accessible name of the control. */
  label: string;
  /** What the button reads with nothing ticked, e.g. "All industries". */
  allLabel: string;
  /** Plural used when several are ticked, e.g. "industries" → "3 industries". */
  noun: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  /** Adds a search box that narrows the list. */
  searchable?: boolean;
  /** Up to this many ticked values the button names them ("Draft, Published"); more are counted. */
  listUpTo?: number;
}) {
  const popover = usePopover();
  const panelId = useId();
  const [needle, setNeedle] = useState('');

  const picked = options.filter((o) => value.includes(o.value));
  const summary = summaryOf(picked, allLabel, noun, listUpTo);
  const shown = needle ? options.filter((o) => o.label.toLowerCase().includes(needle.trim().toLowerCase())) : options;

  function toggle(option: string) {
    onChange(value.includes(option) ? value.filter((v) => v !== option) : [...value, option]);
  }

  return (
    <>
      <button
        ref={popover.button}
        type="button"
        className={`multi-select${picked.length ? ' has-value' : ''}`}
        aria-label={`${label}: ${summary}`}
        aria-haspopup="true"
        aria-expanded={popover.open}
        aria-controls={popover.open ? panelId : undefined}
        onClick={() => {
          setNeedle('');
          popover.toggle();
        }}
      >
        <span className="multi-select-value">{summary}</span>
        <ChevronDown aria-hidden="true" />
      </button>
      {popover.open && (
        <div
          ref={popover.panel}
          id={panelId}
          className="user-dropdown multi-select-panel"
          role="group"
          aria-label={label}
          style={popover.style}
        >
          {searchable && <SearchBox noun={noun} needle={needle} onChange={setNeedle} />}
          <OptionList noun={noun} shown={shown} value={value} onToggle={toggle} />
          <div className="multi-select-foot">
            <button type="button" className="btn btn-ghost btn-xs" disabled={value.length === 0} onClick={() => onChange([])}>
              Clear
            </button>
          </div>
        </div>
      )}
    </>
  );
}
